import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui';
import { EditDrawer } from '../../components/ui/EditDrawer';
import { Icon } from '../../components/icons';
import { getThemeColors } from '../../lib/theme';
import { v2FamilyAccounts, isCareRecipientType } from '../../lib/familyAccounts';
import { v1ProfileOptions, v2LeadQuellen, type V1ProfileOption, type LeadQuelle } from '../../lib/v1';
import { labelOptions, HARDCODED } from '../pflege/profile/options';
import { PhoneInput } from '../pflege/profile/controls';

/**
 * CreateAnfrageDrawer — legt Familienkonto + zugehörigen Kontakt (User +
 * Profil) + Leadquelle in EINEM atomaren Request an (`POST
 * /v2/admin/family-accounts/with-member`, Backend: eine DB::transaction()).
 *
 * Befund 23.09.2026 — bis Batch 13 lief das in bis zu drei getrennten
 * Requests (Konto → Person → Leadquelle-Patch am User). Scheiterte der
 * zweite (z.B. 422 wegen `profiltyp`), blieb ein leeres "Waisenkonto"
 * stehen; ein erneuter Klick legte wegen des rein clientseitigen State
 * ein WEITERES Konto an statt das erste zu vervollständigen (Staging: 8
 * solcher leeren Konten). Der neue Endpunkt validiert Konto+Person VOR
 * jedem Schreibvorgang und schreibt beides nur gemeinsam — bei einem
 * Fehler entsteht gar nichts. `savingRef`/`idempotencyKeyRef` sichern
 * zusätzlich gegen Doppelklick/Netzwerk-Retry ab (siehe deren Docblocks).
 *
 * Rolle (profiltyp): exakt die Liste aus den Profildaten, also
 * `labelOptions('profiltyp', …)` über `v1ProfileOptions.grouped()` — dieselbe
 * Quelle, die auch `FamilyMemberForm` benutzt. Wichtig ist dabei
 * Label-als-Wert: das v2-Backend validiert `profiltyp` gegen
 * `profile_options.value` (StoreMemberRequest), nicht gegen die Options-ID.
 *
 * `is_pflegeempfaenger` wird daraus ABGELEITET (Frau/Mann = Pflegeempfänger,
 * vgl. isCareRecipientType) und nicht mehr hart auf true gesetzt: seit die
 * Rolle auch Rechnungsempfänger/Hausarzt/Kontaktperson sein darf, wäre ein
 * fest gesetztes true schlicht falsch.
 *
 * Quelle: Dropdown aus dem Leadquellen-Katalog (Admin-Einstellungen →
 * Allgemein → Leadquellen), kein Freitext mehr — sonst ist die Herkunft der
 * Anfragen nicht auswertbar. Die Kampagne der gewählten Quelle wird als
 * Vorschlag in „Info" übernommen, bleibt aber überschreibbar.
 */

const EMPTY_FORM = {
  vorname: '', nachname: '', email: '', mobile: '', telefon: '',
  strasse: '', hausnummer: '', plz: '', ort: '', land: '',
  pflegegrad: '' as string,
  profiltyp: '' as string,
  lead_quelle: '', lead_quelle_info: '',
};

/* ─── CreateAnfrageDrawer ───────────────────────────────────────────────────*/
/**
 * Idempotency-Key für POST .../with-member. `crypto.randomUUID()` ist in
 * allen unterstützten Browsern (und Node ≥ 14.17) verfügbar; der Fallback
 * greift nur in exotischen Umgebungen ohne globales `crypto`.
 */
function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `k-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function CreateAnfrageDrawer({ open, dark, onClose, onCreated }: {
  open: boolean; dark: boolean; onClose: () => void; onCreated: () => void;
}) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [opts, setOpts] = useState<Record<string, V1ProfileOption[]> | null>(null);
  const [leadQuellen, setLeadQuellen] = useState<LeadQuelle[]>([]);
  const [form, setForm] = useState({ ...EMPTY_FORM });

  // Zweite Sperre gegen Doppel-Submit ZUSÄTZLICH zu `saving`: React-State-
  // Updates sind asynchron/gebatcht — zwei sehr schnell aufeinanderfolgende
  // Klicks können beide noch `saving === false` sehen, bevor der erste
  // Re-Render mit `saving: true` durchkommt. `useRef` wird sofort
  // synchron gesetzt/gelesen, schließt also genau diese Lücke.
  const savingRef = useRef(false);
  // Bleibt über einen fehlgeschlagenen Versuch hinweg stehen, damit ein
  // manueller Retry (gleicher Klick auf "Anfrage erstellen") denselben
  // Idempotency-Key sendet — genau das macht den Retry serverseitig
  // ungefährlich. Wird nach einem ERFOLGREICHEN Anlegen (oder Abbrechen)
  // zurückgesetzt, damit die nächste Anfrage einen neuen Key bekommt.
  const idempotencyKeyRef = useRef<string | null>(null);

  useEffect(() => {
    let alive = true;
    v1ProfileOptions.grouped()
      .then((g: Record<string, V1ProfileOption[]>) => { if (alive) setOpts(g); })
      .catch(() => { /* labelOptions fällt auf FALLBACK_OPTS.profiltyp zurück */ });
    // Nur aktive Quellen — inaktive gehören nicht ins Anlage-Dropdown.
    v2LeadQuellen.list()
      .then(qs => { if (alive) setLeadQuellen(qs); })
      .catch(() => { /* Dropdown bleibt leer; Anlegen ist ohne Quelle möglich */ });
    return () => { alive = false; };
  }, []);

  // Rolle-Optionen exakt wie in den Profildaten (Label als Wert). Der Fallback
  // für den Fall, dass die Optionen nicht laden, steckt in FALLBACK_OPTS und
  // gilt damit auch für FamilyMemberForm — nicht nur hier.
  const profiltypOptions = useMemo(() => labelOptions('profiltyp', opts), [opts]);

  // Frau/Mann → Pflegeempfänger; alle anderen Rollen sind Kontakte ohne Pflegebedarf.
  const careRecipient = useMemo(
    () => isCareRecipientType(form.profiltyp, profiltypOptions),
    [form.profiltyp, profiltypOptions],
  );

  const selectedQuelle = useMemo(
    () => leadQuellen.find(q => q.name === form.lead_quelle) ?? null,
    [leadQuellen, form.lead_quelle],
  );

  const set = (k: keyof typeof form, v: string) => setForm(f => ({ ...f, [k]: v }));

  /**
   * Quelle wechseln: die Kampagne der Quelle als Info vorschlagen. Ein bereits
   * vom Operator getippter Text bleibt stehen — überschrieben wird nur, was
   * leer ist oder selbst aus einer Kampagne stammt.
   */
  const setQuelle = (name: string) => {
    setForm(f => {
      const prevKampagne = leadQuellen.find(q => q.name === f.lead_quelle)?.kampagne ?? '';
      const nextKampagne = leadQuellen.find(q => q.name === name)?.kampagne ?? '';
      const infoIsAuto = f.lead_quelle_info === '' || f.lead_quelle_info === prevKampagne;
      return {
        ...f,
        lead_quelle: name,
        lead_quelle_info: infoIsAuto ? nextKampagne : f.lead_quelle_info,
      };
    });
  };

  /** Laravel-422-Body auslesen: `{errors:{feld:[meldung,...]}}` → "feld: meldung"-Zeilen. */
  function extractValidationError(e: unknown): string | null {
    const err = e as { response?: { status?: number; data?: { errors?: Record<string, string[]>; message?: string } } };
    if (err?.response?.status !== 422) return null;
    const errors = err.response.data?.errors;
    if (errors && typeof errors === 'object') {
      const lines = Object.entries(errors)
        .map(([field, messages]) => `${field}: ${(messages ?? []).join(' ')}`)
        .filter(Boolean);
      if (lines.length > 0) return lines.join('\n');
    }
    return err.response.data?.message ?? null;
  }

  const save = async () => {
    if (savingRef.current) return; // zweite Sperre, siehe Docblock an savingRef
    if (!form.nachname.trim() && !form.vorname.trim()) {
      setError(tr('anfragen.create.error.name_required'));
      return;
    }
    if (!form.profiltyp) {
      setError(tr('anfragen.create.error.role_required'));
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setError(null);
    if (idempotencyKeyRef.current === null) {
      idempotencyKeyRef.current = newIdempotencyKey();
    }
    try {
      const accountName = [form.nachname, form.vorname].filter(Boolean).join(', ') || form.email;
      // Konto + Person + Leadquelle in EINEM atomaren Request (Backend:
      // eine DB::transaction()) — ersetzt die frühere Kette aus bis zu drei
      // Requests, bei der ein gescheiterter zweiter Schritt ein leeres
      // "Waisenkonto" zurückließ (Befund 23.09.2026).
      await v2FamilyAccounts.createWithMember(
        {
          account: {
            name: accountName,
            lead_quelle: form.lead_quelle || null,
            lead_quelle_info: form.lead_quelle_info || null,
          },
          member: {
            profiltyp: form.profiltyp,
            is_pflegeempfaenger: careRecipient,
            vorname: form.vorname || undefined,
            nachname: form.nachname || undefined,
            email: form.email || undefined,
            mobile: form.mobile || undefined,
            telefon: form.telefon || undefined,
            strasse: form.strasse || undefined,
            hausnummer: form.hausnummer || undefined,
            plz: form.plz || undefined,
            ort: form.ort || undefined,
            land: form.land || undefined,
            // Pflegegrad gibt es nur für Pflegeempfänger (Frau/Mann).
            pflegegrad: careRecipient && form.pflegegrad ? Number(form.pflegegrad) : undefined,
          },
        },
        idempotencyKeyRef.current,
      );
      onCreated();
      setForm({ ...EMPTY_FORM });
      // Erfolgreich abgeschlossen — die nächste Anfrage braucht einen neuen Key.
      idempotencyKeyRef.current = null;
    } catch (e) {
      const baseMessage = extractValidationError(e)
        ?? (e instanceof Error ? e.message : tr('anfragen.create.error.generic'));
      // Der Idempotency-Key bleibt bewusst stehen (kein Reset im catch-Zweig):
      // ein erneuter Klick auf "Anfrage erstellen" sendet denselben Key, das
      // Backend liefert bei einem inzwischen doch erfolgreichen Versuch die
      // ursprüngliche Antwort zurück statt ein zweites Konto anzulegen.
      setError(baseMessage);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const field = (label: string, key: keyof typeof form, type = 'text') => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: t.muted }}>
      {label}
      <input
        type={type}
        value={form[key]}
        onChange={e => set(key, e.target.value)}
        style={{ border: `1px solid ${t.border}`, borderRadius: 8, padding: '8px 10px', background: t.inputBg, color: t.text, fontFamily: 'Outfit,sans-serif', fontSize: 13, outline: 'none' }}
      />
    </label>
  );

  const phoneField = (label: string, key: 'mobile' | 'telefon', placeholder: string) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: t.muted }}>
      {label}
      <PhoneInput dark={dark} value={form[key]} onChange={v => set(key, v)} ariaLabel={label} placeholder={placeholder} />
    </div>
  );

  const selectStyle: React.CSSProperties = {
    border: `1px solid ${t.border}`, borderRadius: 8, padding: '8px 10px',
    background: t.inputBg, color: t.text, fontFamily: 'Outfit,sans-serif', fontSize: 13,
  };

  const sectionLabel = (text: string) => (
    <div style={{ fontSize: 11, fontWeight: 600, color: t.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {text}
    </div>
  );

  return (
    <EditDrawer open={open} onClose={onClose} title={tr('anfragen.create.title')} dark={dark} width={480}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '4px 0' }}>
        {/* Rolle steht oben: sie entscheidet, ob es ein Pflegeempfänger wird. */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: t.muted }}>
            {tr('anfragen.create.field.role')} *
            <select
              aria-label={tr('anfragen.create.field.role')}
              value={form.profiltyp}
              onChange={e => set('profiltyp', e.target.value)}
              style={selectStyle}
            >
              <option value="">{tr('anfragen.create.please_select')}</option>
              {profiltypOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          {careRecipient ? (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: t.muted }}>
              {tr('anfragen.create.field.pflegegrad')}
              <select
                aria-label={tr('anfragen.create.field.pflegegrad')}
                value={form.pflegegrad}
                onChange={e => set('pflegegrad', e.target.value)}
                style={selectStyle}
              >
                <option value="">{tr('anfragen.create.no_pflegegrad')}</option>
                {[1, 2, 3, 4, 5].map(pg => <option key={pg} value={pg}>{tr('anfragen.create.pflegegrad_option', { value: pg })}</option>)}
              </select>
            </label>
          ) : <div />}
        </div>

        <div style={{ borderTop: `1px solid ${t.border}`, paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {sectionLabel(tr('anfragen.create.section.contact'))}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {field(tr('anfragen.create.field.first_name'), 'vorname')}
            {field(tr('anfragen.create.field.last_name'), 'nachname')}
          </div>
          {field(tr('anfragen.create.field.email'), 'email', 'email')}
          {/* Rufnummern wie in den Profildaten: Land (Flagge + Vorwahl) als
              Dropdown plus Nummer, gespeichert als E.164 (+491701234567). */}
          {phoneField(tr('anfragen.create.field.mobile'), 'mobile', '170 1234567')}
          {phoneField(tr('anfragen.create.field.phone'), 'telefon', '89 1234567')}
        </div>

        <div style={{ borderTop: `1px solid ${t.border}`, paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {sectionLabel(tr('anfragen.create.section.address'))}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
            {field(tr('anfragen.create.field.street'), 'strasse')}
            {field(tr('anfragen.create.field.house_number'), 'hausnummer')}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
            {field(tr('anfragen.create.field.zip'), 'plz')}
            {field(tr('anfragen.create.field.city'), 'ort')}
          </div>
          {/* Land als Auswahl mit ISO-Code als Wert — genau wie in den
              Profildaten. Als Freitext („Deutschland") wäre der Wert dort
              später nicht darstellbar und ginge beim nächsten Speichern
              verloren. */}
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: t.muted }}>
            {tr('anfragen.create.field.country')}
            <select
              aria-label={tr('anfragen.create.field.country')}
              value={form.land}
              onChange={e => set('land', e.target.value)}
              style={selectStyle}
            >
              <option value="">{tr('anfragen.create.please_select')}</option>
              {HARDCODED.land.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
        </div>

        <div style={{ borderTop: `1px solid ${t.border}`, paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {sectionLabel(tr('anfragen.create.section.lead_source'))}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: t.muted }}>
              {tr('anfragen.create.field.source')}
              <select
                aria-label={tr('anfragen.create.field.source')}
                value={form.lead_quelle}
                onChange={e => setQuelle(e.target.value)}
                style={selectStyle}
              >
                <option value="">{tr('anfragen.create.no_source')}</option>
                {leadQuellen.map(q => (
                  <option key={q.id} value={q.name}>
                    {q.icon ? `${q.icon} ${q.name}` : q.name}
                  </option>
                ))}
              </select>
            </label>
            {field(tr('anfragen.create.field.info'), 'lead_quelle_info')}
          </div>
          {leadQuellen.length === 0 && (
            <div style={{ fontSize: 11, color: t.muted }}>
              {tr('anfragen.create.no_sources_hint')}
            </div>
          )}
          {selectedQuelle?.kampagne && (
            <div style={{ fontSize: 11, color: t.muted }}>
              {tr('anfragen.create.source_campaign', { campaign: selectedQuelle.kampagne })}
            </div>
          )}
        </div>

        {error && <div style={{ fontSize: 12, color: '#b42318', fontWeight: 500, whiteSpace: 'pre-line' }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8, paddingTop: 4 }}>
          <Button variant="primary" dark={dark} onClick={save} disabled={saving} icon={<Icon.Check c="#fff"/>}>
            {saving ? tr('anfragen.create.saving') : tr('anfragen.create.submit')}
          </Button>
          <Button variant="secondary" dark={dark} onClick={onClose}>{tr('common.action.cancel')}</Button>
        </div>
      </div>
    </EditDrawer>
  );
}
